/**
 * Lazily loaded stand-in for `@sentry/react`.
 *
 * The SDK is 114 KiB of the vendor chunk, and the vendor chunk is what the
 * hero image queues behind on a phone. Nothing about error reporting has to
 * happen before the page paints, so the SDK is fetched after LCP and every
 * call made in the meantime is replayed into it on arrival.
 *
 * The queue records intent rather than results. Almost every `withScope` call
 * in this app configures the scope and then reports through the module-level
 * `captureException`/`captureMessage` from inside the same callback, so a
 * recorded scope has to capture both halves as one ordered unit: replaying the
 * report outside its scope would strip the level, tags, fingerprint and extras
 * it was meant to carry — exactly the context that makes a hydration report
 * readable. Calls made on the server are dropped, matching `@sentry/react`,
 * whose namespace has no callable exports under Node.
 *
 * Two constraints come with deferral, and callers have to respect them:
 * a `withScope` callback must do reporting and nothing else, because on the
 * server it never runs at all; and nesting `withScope` inside another
 * `withScope` is not supported — the inner one records a separate entry
 * instead of inheriting the outer scope.
 */

type SentryModule = typeof import('@sentry/react');
type RealScope = InstanceType<SentryModule['Scope']>;

export type SentryInitOptions = Parameters<SentryModule['init']>[0];

/**
 * The subset of `Scope` this app uses. Methods return `void` where the real
 * scope returns `this`; chaining is not available through the facade.
 */
export interface SentryScope {
  setTag(key: string, value: Parameters<RealScope['setTag']>[1]): void;
  setExtra(key: string, value: unknown): void;
  setExtras(extras: Parameters<RealScope['setExtras']>[0]): void;
  setLevel(level: Parameters<RealScope['setLevel']>[0]): void;
  setContext(
    key: string,
    context: Parameters<RealScope['setContext']>[1],
  ): void;
  setFingerprint(fingerprint: string[]): void;
  captureMessage(
    message: string,
    level?: Parameters<RealScope['setLevel']>[0],
  ): void;
}

/**
 * One recorded call inside a deferred `withScope`, tagged with what it has to
 * run against on replay: the scope handed out by the SDK, or the SDK itself.
 */
type RecordedStep = {
  target: 'scope' | 'sdk';
  method: string;
  args: unknown[];
};

type QueuedEntry =
  | { kind: 'call'; method: string; args: unknown[] }
  | { kind: 'scope'; steps: RecordedStep[] };

type AnyMethods = Record<string, (...args: unknown[]) => void>;

/**
 * A page that reports steadily while the SDK never arrives must not grow
 * without bound; the oldest entries are the ones worth dropping.
 */
const MAX_QUEUED_ENTRIES = 100;

/**
 * Breadcrumbs queue apart from reports, and drop the oldest rather than the
 * newest.
 *
 * They share neither budget nor discard rule with events on purpose. A
 * checkout traces a few dozen steps, so on one queue the flow would evict the
 * failure it exists to explain — the reverse of what either is for. Dropping
 * from the front also matches what a breadcrumb is worth: the steps just
 * before a failure are the ones that describe it, and the SDK's own default
 * keeps a comparable window.
 */
const MAX_QUEUED_BREADCRUMBS = 50;

const queue: QueuedEntry[] = [];
const breadcrumbQueue: unknown[][] = [];
let sdk: SentryModule | null = null;
let loading = false;
let failed = false;

/** Non-null only while a deferred `withScope` callback is running. */
let recordingSteps: RecordedStep[] | null = null;

const isBrowser = typeof window !== 'undefined';

const invoke = (target: unknown, method: string, args: unknown[]) => {
  const fn = (target as AnyMethods)[method];
  if (typeof fn !== 'function') {
    if (import.meta.env.DEV) {
      console.error(`[sentry] facade called a missing SDK method: ${method}`);
    }
    return;
  }
  fn.apply(target, args);
};

/**
 * Detaches a payload from the state it was read out of.
 *
 * Real Sentry serialises an event in the same tick it is captured. The facade
 * replays seconds later, by which time a store field handed in as an extra has
 * usually moved on — a report meant to show the state before a change would
 * otherwise show the state after it. Errors are passed through untouched so
 * their stack survives.
 */
const snapshot = (value: unknown): unknown => {
  if (
    sdk ||
    value instanceof Error ||
    value === null ||
    typeof value !== 'object'
  ) {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    try {
      return structuredClone(value);
    } catch {
      return value;
    }
  }
};

let dropped = 0;

const push = (entry: QueuedEntry): boolean => {
  if (queue.length >= MAX_QUEUED_ENTRIES) {
    // Counted rather than ignored: a render loop can produce hundreds of
    // reports in seconds, and an issue that silently shows the first hundred
    // reads as a much smaller problem than it is.
    dropped += 1;
    return false;
  }
  queue.push(entry);
  return true;
};

/** Records what the caller did to the scope so it can be replayed later. */
const recordingScope = (steps: RecordedStep[]): SentryScope => {
  const record =
    (method: string) =>
    (...args: unknown[]) => {
      steps.push({ target: 'scope', method, args: args.map(snapshot) });
    };

  return {
    setTag: record('setTag'),
    setExtra: record('setExtra'),
    setExtras: record('setExtras'),
    setLevel: record('setLevel'),
    setContext: record('setContext'),
    setFingerprint: record('setFingerprint'),
    captureMessage: record('captureMessage'),
  };
};

/**
 * Replays the queue into the SDK.
 *
 * Every entry runs inside its own scope carrying `deferred_replay`, because a
 * replayed event is stamped with the time and URL of the replay rather than of
 * the moment it happened — without the tag those few seconds of skew are
 * invisible in the issue. One entry that throws must not take the rest of the
 * queue with it, so each is isolated.
 */
const flush = () => {
  const loaded = sdk;
  if (!loaded) {
    return;
  }
  if (dropped) {
    const lost = dropped;
    dropped = 0;
    try {
      loaded.captureMessage(
        `[sentry] queue overflowed before the SDK arrived — ${lost} report(s) dropped`,
      );
    } catch {
      // Reporting the overflow must not cost the reports that survived it.
    }
  }

  for (const args of breadcrumbQueue.splice(0)) {
    try {
      invoke(loaded, 'addBreadcrumb', args);
    } catch {
      // A malformed crumb is not worth the trail it sits in.
    }
  }

  for (const entry of queue.splice(0)) {
    try {
      loaded.withScope((scope) => {
        (scope as unknown as AnyMethods).setTag?.('deferred_replay', 'true');
        if (entry.kind === 'call') {
          invoke(loaded, entry.method, entry.args);
          return;
        }
        for (const step of entry.steps) {
          invoke(
            step.target === 'scope' ? scope : loaded,
            step.method,
            step.args,
          );
        }
      });
    } catch {
      // One malformed payload must not silence the rest of the queue.
    }
  }
};

const enqueue = (method: string, args: unknown[]): void => {
  if (!isBrowser || failed) {
    return;
  }
  if (sdk) {
    invoke(sdk, method, args);
    return;
  }
  if (recordingSteps) {
    recordingSteps.push({ target: 'sdk', method, args });
    return;
  }
  push({ kind: 'call', method, args });
};

/** Gives up permanently: nothing will make this build's SDK arrive. */
const abandon = () => {
  failed = true;
  queue.length = 0;
  breadcrumbQueue.length = 0;
};

/**
 * Downloads the SDK and replays everything recorded so far.
 *
 * `options` may be a factory so that integrations, which have to be
 * constructed from the module itself, can be declared by the caller without
 * importing `@sentry/react` statically — a static import would put the SDK
 * back in the critical chunk and undo the whole point.
 *
 * Either half can fail: a dynamic import breaks when a deploy replaces the
 * build the page was served from (see `utils/staleChunk.ts`), and `init`
 * breaks on a malformed config. Neither has anywhere to report to, so both
 * abandon the queue rather than grow it for the rest of the session.
 */
export const load = async (
  options: SentryInitOptions | ((module: SentryModule) => SentryInitOptions),
): Promise<void> => {
  if (!isBrowser || loading || sdk || failed) {
    return;
  }
  loading = true;

  let loaded: SentryModule;
  try {
    loaded = await import('@sentry/react');
  } catch {
    // A failed chunk fetch is not final — the page may be restored from
    // bfcache, or the next trigger may run on a working connection. The queue
    // is capped, so waiting costs nothing unbounded.
    loading = false;
    return;
  }

  try {
    loaded.init(typeof options === 'function' ? options(loaded) : options);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[sentry] init failed', error);
    }
    abandon();
    return;
  }

  sdk = loaded;
  flush();
};

/** True once the SDK is initialised and calls go straight through. */
export const isLoaded = (): boolean => sdk !== null;

export const withScope = (callback: (scope: SentryScope) => void): void => {
  if (!isBrowser || failed) {
    return;
  }
  if (sdk) {
    sdk.withScope(callback);
    return;
  }

  // The entry is queued before the callback runs so that a callback which
  // throws mid-way still delivers what it had already reported.
  const steps: RecordedStep[] = [];
  push({ kind: 'scope', steps });

  const outer = recordingSteps;
  recordingSteps = steps;
  try {
    callback(recordingScope(steps));
  } finally {
    recordingSteps = outer;
  }
};

export const captureMessage = (message: string, context?: unknown): void =>
  enqueue(
    'captureMessage',
    context === undefined ? [message] : [message, snapshot(context)],
  );

export const captureException = (error: unknown, context?: unknown): void =>
  enqueue(
    'captureException',
    context === undefined ? [error] : [error, snapshot(context)],
  );

export const addBreadcrumb = (
  breadcrumb: Parameters<SentryModule['addBreadcrumb']>[0],
): void => {
  if (!isBrowser || failed) {
    return;
  }
  if (sdk) {
    invoke(sdk, 'addBreadcrumb', [breadcrumb]);
    return;
  }
  if (breadcrumbQueue.length >= MAX_QUEUED_BREADCRUMBS) {
    breadcrumbQueue.shift();
  }
  breadcrumbQueue.push([snapshot(breadcrumb)]);
};

export const setUser = (user: Parameters<SentryModule['setUser']>[0]): void =>
  enqueue('setUser', [snapshot(user)]);

export const setTag = (
  key: string,
  value: Parameters<RealScope['setTag']>[1],
): void => enqueue('setTag', [key, value]);
